// #region Imports
import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchTerminalReceptionReport,
    patchBooking,
    resetTerminalReception,
    resetPatchBookingStatus,
    selectTerminalReceptionData,
    selectTerminalReceptionStatus,
    selectTerminalReceptionError,
    selectPatchBookingStatus,
} from 'redux/slices/sla-reports/terminal-reception';
import { setToast, setShowToast } from '_redux/slices/timeline/timeline-toast';
import TerminalReceptionSummary from './terminal-reception-summary';
import TerminalReceptionRawDataTable from './terminal-reception-raw-data-table';
import ReportHeader from './report-header';
// #endregion

const TerminalReceptionReport = ({ dateRange, iata, runReportRef, onRunLoadingChange, hideBreakdown }) => {
    const [includeNoData, setIncludeNoData] = useState(false);
    const [overrideRow, setOverrideRow] = useState(null);
    const dispatch = useDispatch();
    const data = useSelector(selectTerminalReceptionData);
    const status = useSelector(selectTerminalReceptionStatus);
    const error = useSelector(selectTerminalReceptionError);
    const patchStatus = useSelector(selectPatchBookingStatus);
    const isLoading = status === 'loading';
    const isPatching = patchStatus === 'loading';

    const handleRunReport = useCallback(() => {
        const startDate = dateRange?.[0];
        const endDate = dateRange?.[1];

        if (!startDate || !endDate || !iata) return;

        const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
        const formattedStartDate = toLocalDateString(startDate);
        const formattedEndDate = toLocalDateString(endDate);

        dispatch(
            fetchTerminalReceptionReport({
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                iata,
                hideBreakdown,
            })
        );
    }, [dispatch, dateRange, iata, hideBreakdown]);

    useEffect(() => {
        return () => {
            dispatch(resetTerminalReception());
        };
    }, [dispatch]);

    useEffect(() => {
        if (patchStatus === 'succeeded') {
            dispatch(setToast({ message: 'Booking updated successfully', statusType: 'success' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchBookingStatus());
            setOverrideRow(null);
            const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
            const startDate = dateRange?.[0] ? toLocalDateString(dateRange[0]) : null;
            const endDate = dateRange?.[1] ? toLocalDateString(dateRange[1]) : null;
            if (startDate && endDate && iata) {
                dispatch(fetchTerminalReceptionReport({ startDate, endDate, iata, hideBreakdown }));
            }
        }
    }, [patchStatus, dispatch, dateRange, iata, hideBreakdown]);

    useEffect(() => {
        if (patchStatus === 'failed') {
            dispatch(setToast({ message: 'Failed to update booking', statusType: 'failed' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchBookingStatus());
        }
    }, [patchStatus, dispatch]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isLoading);
    }, [isLoading, onRunLoadingChange]);

    const dailyReports = data?.dailyReports ?? [];
    const isMultiDay = (data?.dateRange?.length ?? 0) > 1;

    return (
        <div>
            <ReportHeader
                onRunReport={handleRunReport}
                isLoading={isLoading}
                hideRunButton={!!runReportRef}
            />

            {status === 'failed' && (
                <p style={{ color: '#991b1b', fontSize: '13px' }}>Error loading Terminal Reception report: {error}</p>
            )}

            {data && (
                <>
                    <TerminalReceptionSummary
                        dailyReports={dailyReports}
                        overallSummary={isMultiDay ? data.summary : null}
                        includeNoData={includeNoData}
                        isMultiDay={isMultiDay}
                    />
                    {!hideBreakdown && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '24px', marginBottom: '12px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#4b5563', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={includeNoData}
                                        onChange={(e) => setIncludeNoData(e.target.checked)}
                                        style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#3b82f6' }}
                                    />
                                    Include no-data (skip) rows
                                </label>
                            </div>
                            <TerminalReceptionRawDataTable
                                dailyReports={dailyReports}
                                includeNoData={includeNoData}
                                overrideRow={overrideRow}
                                onOverrideRowChange={setOverrideRow}
                                onPatchBooking={(row, pass, reason, kpiKey) => {
                                    dispatch(patchBooking({
                                        bookingId: row.bookingId,
                                        patch: {
                                            terminalReceptionReportOverride: {
                                                [kpiKey]: { pass, passReason: reason },
                                            },
                                        },
                                    }));
                                }}
                                isPatching={isPatching}
                            />
                        </>
                    )}
                </>
            )}

        </div>
    );
};

export default TerminalReceptionReport;
